/**
 * Client Management API
 * API functions for Phase 4 Client Management
 */

import axios from 'axios';
import type {
  Client,
  ClientFilters,
  ClientAnalytics,
  CreateClientInput,
  UpdateClientInput,
  Order,
  OrderFilters,
  CreateOrderInput,
  UpdateOrderInput,
  PaginatedResponse,
  ApiResponse
} from '../types/client';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';
const CLIENT_BASE = `${API_BASE_URL}/ai-crm`;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: CLIENT_BASE,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle response errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ===== CLIENT APIs =====

export const clientApi = {
  /**
   * Get all clients with optional filters
   */
  getClients: async (filters?: ClientFilters): Promise<PaginatedResponse<Client>> => {
    const response = await apiClient.get<PaginatedResponse<Client>>('/clients', {
      params: filters
    });
    return response.data;
  },

  /**
   * Get client by ID
   */
  getClientById: async (id: string): Promise<Client> => {
    const response = await apiClient.get<ApiResponse<Client>>(`/clients/${id}`);
    return response.data.data;
  },

  /**
   * Create new client (direct creation without lead conversion)
   */
  createClient: async (data: CreateClientInput): Promise<Client> => {
    const response = await apiClient.post<ApiResponse<Client>>('/clients', data);
    return response.data.data;
  },

  /**
   * Update client
   */
  updateClient: async (id: string, data: UpdateClientInput): Promise<Client> => {
    const response = await apiClient.put<ApiResponse<Client>>(`/clients/${id}`, data);
    return response.data.data;
  },

  /**
   * Delete client
   */
  deleteClient: async (id: string): Promise<void> => {
    await apiClient.delete(`/clients/${id}`);
  },

  /**
   * Update client status
   */
  updateClientStatus: async (id: string, status: string): Promise<Client> => {
    const response = await apiClient.put<ApiResponse<Client>>(`/clients/${id}/status`, { status });
    return response.data.data;
  },

  /**
   * Update client tier
   */
  updateClientTier: async (id: string, tier: string): Promise<Client> => {
    const response = await apiClient.put<ApiResponse<Client>>(`/clients/${id}/tier`, { tier });
    return response.data.data;
  },

  /**
   * Assign staff to client
   */
  assignClientStaff: async (id: string, assignedToUserId: string | null): Promise<Client> => {
    const response = await apiClient.put<ApiResponse<Client>>(`/clients/${id}/assign`, { assignedToUserId });
    return response.data.data;
  },

  /**
   * Get client analytics
   */
  getClientAnalytics: async (id: string): Promise<ClientAnalytics> => {
    const response = await apiClient.get<ApiResponse<ClientAnalytics>>(`/clients/${id}/analytics`);
    return response.data.data;
  },

  /**
   * Get at-risk clients
   */
  getAtRiskClients: async (riskThreshold = 70, limit = 20): Promise<Client[]> => {
    const response = await apiClient.get<ApiResponse<Client[]>>('/clients/at-risk', {
      params: { riskThreshold, limit }
    });
    return response.data.data;
  },

  /**
   * Get upsell opportunities
   */
  getUpsellOpportunities: async (potentialThreshold = 70, limit = 20): Promise<Client[]> => {
    const response = await apiClient.get<ApiResponse<Client[]>>('/clients/upsell-opportunities', {
      params: { potentialThreshold, limit }
    });
    return response.data.data;
  }
};

// ===== ORDER APIs =====

export const orderApi = {
  /**
   * Get orders for a client
   */
  getClientOrders: async (clientId: string, filters?: OrderFilters): Promise<PaginatedResponse<Order>> => {
    const response = await apiClient.get<PaginatedResponse<Order>>(`/clients/${clientId}/orders`, {
      params: filters
    });
    return response.data;
  },

  /**
   * Create order for client
   */
  createOrder: async (clientId: string, data: CreateOrderInput): Promise<Order> => {
    const response = await apiClient.post<ApiResponse<Order>>(`/clients/${clientId}/orders`, data);
    return response.data.data;
  },

  /**
   * Update order
   */
  updateOrder: async (id: string, data: UpdateOrderInput): Promise<Order> => {
    const response = await apiClient.put<ApiResponse<Order>>(`/orders/${id}`, data);
    return response.data.data;
  },

  /**
   * Delete order
   */
  deleteOrder: async (id: string): Promise<void> => {
    await apiClient.delete(`/orders/${id}`);
  }
};

export const clientActivitiesApi = {
  getActivities: async (clientId: string): Promise<any[]> => {
    const response = await apiClient.get<ApiResponse<any[]>>(`/clients/${clientId}/activities`);
    return response.data.data;
  },
  addActivity: async (
    clientId: string,
    payload: {
      type: 'CALL' | 'EMAIL' | 'MEETING' | 'NOTE' | string;
      subject?: string;
      content: string;
      metadata?: { callDuration?: number; meetingDate?: Date | string; emailSubject?: string };
    },
  ): Promise<any> => {
    // Serialise Date -> ISO so axios doesn't drop the timezone.
    const body = {
      ...payload,
      metadata: payload.metadata
        ? {
            ...payload.metadata,
            meetingDate:
              payload.metadata.meetingDate instanceof Date
                ? payload.metadata.meetingDate.toISOString()
                : payload.metadata.meetingDate,
          }
        : undefined,
    };
    const response = await apiClient.post<ApiResponse<any>>(`/clients/${clientId}/activities`, body);
    return response.data.data;
  },
};

// Export combined API
export const crmClientApi = {
  clients: clientApi,
  orders: orderApi
};

export default crmClientApi;
